import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
  signal,
} from '@angular/core';
import type { ImageRefDto } from '../../shared/models/marketing.models';
import { RouterLink } from '@angular/router';
import { createH1SliderSwipeHandlers, sliderTrackTransform } from '../utils/h1-slider-swipe';
import { parseMilestoneCtaLink, resolveMilestoneCtaHref } from '../utils/milestone-cta';

export type GlobalSuccessStoryModel = {
  pill?: string;
  title: string;
  description: string;
  ctaLabel?: string;
  ctaHref?: string;
  image?: ImageRefDto;
};

@Component({
  selector: 'app-marketing-global-success-stories',
  standalone: true,
  imports: [RouterLink],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Let .h1-slider siblings stack like /home1 (adjacent-sibling spacing in _home1.scss).
  host: { style: 'display: contents' },
  template: `
    @if (stories.length) {
      <div class="h1-slider" role="region" aria-roledescription="carousel" aria-label="Featured milestones">
        <div
          #viewport
          class="h1-slider__viewport"
          [class.h1-slider__viewport--dragging]="isDragging()"
          (pointerdown)="swipe.onPointerDown($event)"
          (pointermove)="swipe.onPointerMove($event)"
          (pointerup)="swipe.onPointerUp($event)"
          (pointercancel)="swipe.onPointerCancel($event)"
        >
          <div
            class="h1-slider__track"
            [class.h1-slider__track--dragging]="isDragging()"
            [style.transform]="trackTransform()"
          >
            @for (story of stories; track story.title; let i = $index) {
              <div class="h1-slider__slide" [attr.aria-hidden]="activeSlide() !== i">
                <article class="h1-milestone">
                  <div class="h1-milestone__copy">
                    @if (story.pill) {
                      <span class="h1-milestone__pill">{{ story.pill }}</span>
                    }
                    <h3>
                      @for (line of story.title.split('\n'); track $index) {
                        {{ line }}
                        @if (!$last) {
                          <br />
                        }
                      }
                    </h3>
                    <p>{{ story.description }}</p>
                    @if (story.ctaLabel; as label) {
                      @if (milestoneCtaHref(story); as href) {
                        @let link = parseMilestoneCtaLink(href);
                        @if (link.kind === 'router') {
                          <a
                            [routerLink]="link.path"
                            [fragment]="link.fragment"
                            class="h1-milestone__cta"
                          >
                            {{ label }}
                            <span aria-hidden="true">→</span>
                          </a>
                        } @else {
                          <a [href]="link.href" class="h1-milestone__cta">
                            {{ label }}
                            <span aria-hidden="true">→</span>
                          </a>
                        }
                      }
                    }
                  </div>
                  <div class="h1-milestone__media" aria-hidden="true">
                    @if (story.image?.cdnUrl; as imageUrl) {
                      <img [src]="imageUrl" [alt]="story.image?.alt ?? ''" loading="lazy" draggable="false" />
                    }
                  </div>
                </article>
              </div>
            }
          </div>
        </div>

        <div class="h1-slider__controls">
          <button
            type="button"
            class="h1-slider__arrow"
            (click)="prevSlide()"
            [disabled]="activeSlide() === 0"
            aria-label="Previous milestone"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
              stroke-linecap="round" stroke-linejoin="round" focusable="false">
              <path d="m15 18-6-6 6-6"></path>
            </svg>
          </button>

          <div class="h1-slider__dots" role="tablist" aria-label="Select milestone slide">
            @for (story of stories; track $index) {
              <button
                type="button"
                role="tab"
                class="h1-slider__dot"
                [class.is-active]="activeSlide() === $index"
                [attr.aria-selected]="activeSlide() === $index"
                [attr.aria-label]="'Show milestone ' + ($index + 1)"
                (click)="setSlide($index)"
              ></button>
            }
          </div>

          <button
            type="button"
            class="h1-slider__arrow"
            (click)="nextSlide()"
            [disabled]="activeSlide() === stories.length - 1"
            aria-label="Next milestone"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
              stroke-linecap="round" stroke-linejoin="round" focusable="false">
              <path d="m9 18 6-6-6-6"></path>
            </svg>
          </button>
        </div>
      </div>
    }
  `,
})
export class MarketingGlobalSuccessStoriesComponent implements OnChanges {
  readonly parseMilestoneCtaLink = parseMilestoneCtaLink;

  @Input() stories: GlobalSuccessStoryModel[] = [];

  milestoneCtaHref(story: GlobalSuccessStoryModel): string | undefined {
    return resolveMilestoneCtaHref(story.ctaHref, story.title);
  }

  @ViewChild('viewport') private viewport?: ElementRef<HTMLElement>;

  readonly activeSlide = signal(0);
  readonly dragOffsetPx = signal(0);
  readonly isDragging = signal(false);

  readonly swipe = createH1SliderSwipeHandlers({
    activeSlide: this.activeSlide,
    slideCount: () => this.stories.length,
    viewportWidth: () => this.viewport?.nativeElement.clientWidth ?? 0,
    dragOffsetPx: this.dragOffsetPx,
    isDragging: this.isDragging,
  });

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['stories']) {
      const max = Math.max(0, this.stories.length - 1);
      if (this.activeSlide() > max) {
        this.activeSlide.set(max);
      }
    }
  }

  trackTransform(): string {
    return sliderTrackTransform(this.activeSlide(), this.dragOffsetPx());
  }

  prevSlide(): void {
    this.activeSlide.update((s) => Math.max(0, s - 1));
  }

  nextSlide(): void {
    this.activeSlide.update((s) => Math.min(this.stories.length - 1, s + 1));
  }

  setSlide(index: number): void {
    this.activeSlide.set(index);
  }
}
